import type {
  CreatePurchaseRequest,
  CreatePurchaseResponse,
  PublicEvent,
  TransactionStatusResponse,
} from "@evka/shared";

// Empty default → same-origin (production: backend serves the SPA).
// In dev, Vite's proxy forwards /api/* to localhost:3000.
const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/+$/, "");

interface ApiErrorBody {
  error?: { code: string; message: string; details?: unknown };
}

export class ApiError extends Error {
  public readonly status: number;
  public readonly code: string;
  public readonly details: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

async function parseError(res: Response): Promise<ApiError> {
  let code = "http_error";
  let message = `HTTP ${res.status}`;
  let details: unknown;
  try {
    const body = (await res.json()) as ApiErrorBody;
    if (body?.error) {
      code = body.error.code ?? code;
      message = body.error.message ?? message;
      details = body.error.details;
    }
  } catch {
    // Non-JSON body — fall through to the default message.
  }
  return new ApiError(res.status, code, message, details);
}

async function jsonRequest<T>(path: string, init?: RequestInit): Promise<T> {
  // Only declare a JSON content type when we're actually sending one.
  // Fastify's default JSON parser rejects requests where Content-Type is
  // application/json but the body is empty (FST_ERR_CTP_EMPTY_JSON_BODY),
  // which is exactly what a body-less POST (e.g. /reconcile) would trip.
  const hasBody = init?.body != null;
  const res = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers: {
      Accept: "application/json",
      ...(hasBody ? { "Content-Type": "application/json" } : {}),
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) throw await parseError(res);
  return (await res.json()) as T;
}

export function getEvent(eventId: string): Promise<PublicEvent> {
  return jsonRequest<PublicEvent>(`/api/events/${encodeURIComponent(eventId)}`);
}

export function createPurchase(
  body: CreatePurchaseRequest,
): Promise<CreatePurchaseResponse> {
  return jsonRequest<CreatePurchaseResponse>("/api/purchases", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function getTransactionStatus(
  transactionId: string,
  token: string,
): Promise<TransactionStatusResponse> {
  const qs = new URLSearchParams({ token }).toString();
  return jsonRequest<TransactionStatusResponse>(
    `/api/transactions/${encodeURIComponent(transactionId)}/status?${qs}`,
  );
}

export function reconcileTransaction(
  transactionId: string,
  token: string,
): Promise<TransactionStatusResponse> {
  const qs = new URLSearchParams({ token }).toString();
  return jsonRequest<TransactionStatusResponse>(
    `/api/transactions/${encodeURIComponent(transactionId)}/reconcile?${qs}`,
    { method: "POST" },
  );
}
